/**
 * Constants of the customer Community. Where a value mirrors one on the backend it says so, because the two must change together.
 */

/** The four tabs of the Community page; the strings are the backend's `CustomerQuestionTabs`. */
export const COMMUNITY_TABS = ['trending', 'latest', 'verified', 'mine'] as const;
export type CommunityTab = (typeof COMMUNITY_TABS)[number];
export const COMMUNITY_DEFAULT_TAB: CommunityTab = 'trending';

/** Questions per page. The server clamps whatever is asked (`CommunityOptions.MaxTake`); 10 is the design's page size. */
export const COMMUNITY_PAGE_SIZE = 10;

/** Answers per page and replies per page on a question; the server's own defaults (`ThreadPageSize`, `ReplyPageSize`). */
export const COMMUNITY_ANSWER_PAGE_SIZE = 10;
export const COMMUNITY_REPLY_PAGE_SIZE = 5;

/** How long a deleted answer or reply can still be brought back before the delete is sent. */
export const COMMUNITY_DELETE_UNDO_MS = 6000;

/**
 * Search limits, mirroring the backend (`CommunityOptions.MinSearchTermLength`, `CommunityConstants.SearchMaxTerms` and
 * `SearchTextMaxLength`). They only decide what is worth sending; the server enforces them regardless.
 */
export const COMMUNITY_SEARCH_MIN_WORD_LENGTH = 3;
export const COMMUNITY_SEARCH_MAX_WORDS = 8;
export const COMMUNITY_SEARCH_MAX_LENGTH = 100;

/**
 * Wait after the last keystroke before asking for suggestions. The server allows 60 suggestion requests a minute per member, so
 * this is what keeps ordinary typing well inside it.
 */
export const COMMUNITY_SUGGEST_DEBOUNCE_MS = 300;

/** Error codes the Community backend answers with, in `error.error.error.code` of the HTTP error. */
export const COMMUNITY_ERROR = {
    featureDisabled: 'ERR_COMMUNITY_FEATURE_DISABLED',
    subscriptionInactive: 'ERR_COMMUNITY_SUBSCRIPTION_INACTIVE',
    termsRequired: 'ERR_COMMUNITY_TERMS_REQUIRED',
    questionNotFound: 'ERR_COMMUNITY_QUESTION_NOT_FOUND',
    searchInvalid: 'ERR_COMMUNITY_SEARCH_INVALID',
    rateLimited: 'ERR_COMMUNITY_RATE_LIMITED',
    contentBlocked: 'ERR_COMMUNITY_CONTENT_BLOCKED',
    replyDepth: 'ERR_COMMUNITY_REPLY_DEPTH',
    cannotReportOwn: 'ERR_COMMUNITY_CANNOT_REPORT_OWN',
    cannotReportOfficial: 'ERR_COMMUNITY_CANNOT_REPORT_OFFICIAL',
    reportCategoryInvalid: 'ERR_COMMUNITY_REPORT_CATEGORY_INVALID',
    attachmentTypeNotAllowed: 'ERR_COMMUNITY_ATTACHMENT_TYPE_NOT_ALLOWED',
    attachmentCountExceeded: 'ERR_COMMUNITY_ATTACHMENT_COUNT_EXCEEDED',
    attachmentSizeExceeded: 'ERR_COMMUNITY_ATTACHMENT_SIZE_EXCEEDED',
    attachmentInvalid: 'ERR_COMMUNITY_ATTACHMENT_INVALID',
    attachmentEmpty: 'ERR_COMMUNITY_ATTACHMENT_EMPTY',
    titleRequired: 'ERR_COMMUNITY_TITLE_REQUIRED',
    bodyRequired: 'ERR_COMMUNITY_BODY_REQUIRED',
    titleTooLong: 'ERR_COMMUNITY_TITLE_TOO_LONG',
    bodyTooLong: 'ERR_COMMUNITY_BODY_TOO_LONG',
    reportDetailsTooLong: 'ERR_COMMUNITY_REPORT_DETAILS_TOO_LONG',
    topicRequired: 'ERR_COMMUNITY_TOPIC_REQUIRED',
    topicNotFound: 'ERR_COMMUNITY_TOPIC_NOT_FOUND',
} as const;

/**
 * Limits of what a member can write. Client-side UX only: the server enforces its own (`CommunityOptions`), and these exist so the member
 * is told before sending rather than after.
 */
export const COMMUNITY_TITLE_MAX_LENGTH = 150;
export const COMMUNITY_BODY_MAX_LENGTH = 10_000;
export const COMMUNITY_REPORT_DETAILS_MAX_LENGTH = 1000;
/** The longest `?title=` prefill of the ask form taken from the URL (the search box's own limit). */
export const COMMUNITY_PREFILL_TITLE_MAX_LENGTH = COMMUNITY_SEARCH_MAX_LENGTH;

/** Attachment limits: at most 5 files, 50 MB in total (1 MB = 1,000,000 bytes), by extension. */
export const COMMUNITY_ATTACHMENT_MAX_FILES = 5;
export const COMMUNITY_ATTACHMENT_BYTES_PER_MB = 1_000_000;
export const COMMUNITY_ATTACHMENT_MAX_TOTAL_MB = 50;
export const COMMUNITY_ATTACHMENT_MAX_TOTAL_BYTES =
    COMMUNITY_ATTACHMENT_MAX_TOTAL_MB * COMMUNITY_ATTACHMENT_BYTES_PER_MB;
export const COMMUNITY_ATTACHMENT_EXTENSIONS: readonly string[] = ['png', 'jpg', 'jpeg', 'pdf', 'doc', 'docx'];
/** The value of the file input's `accept` attribute; a convenience for the picker only, never relied on for validation. */
export const COMMUNITY_ATTACHMENT_ACCEPT = COMMUNITY_ATTACHMENT_EXTENSIONS.map((extension) => `.${extension}`).join(
    ',',
);

/** The report reasons, exactly the codes the backend accepts. */
export const COMMUNITY_REPORT_CATEGORIES = [
    'profanity',
    'abuse',
    'spam',
    'privacy',
    'harassment',
    'hate',
    'misinformation',
    'inappropriate',
    'other',
] as const;
export type CommunityReportCategory = (typeof COMMUNITY_REPORT_CATEGORIES)[number];

/** Error codes of accepting the terms (`ERR_TC_*`). */
export const TERMS_ERROR = {
    notFound: 'ERR_TC_NOT_FOUND',
    notLatest: 'ERR_TC_NOT_LATEST',
    hashMismatch: 'ERR_TC_HASH_MISMATCH',
    readRequired: 'ERR_TC_READ_REQUIRED',
} as const;

/** Prefixes of the codes the HTTP interceptor hands to the Community pages untouched, so they can react to the exact code. */
export const COMMUNITY_ERROR_CODE_PREFIXES = ['ERR_COMMUNITY_', 'ERR_TC_'] as const;

/**
 * Topic slug (the backend's `CommunityTopic.IdentifierName`) to its icon. A topic without an entry shows the "others" icon. A map rather
 * than an object because the slugs contain hyphens, which are not valid property names under the project's naming rule.
 */
export const COMMUNITY_TOPIC_ICONS: ReadonlyMap<string, string> = new Map([
    ['e-invoicing', 'assets/images/community/icon-einvoicing.svg'],
    ['sst', 'assets/images/community/icon-sst.svg'],
    ['rpgt', 'assets/images/community/icon-rpgt.svg'],
    ['personal-tax', 'assets/images/community/icon-personaltax.svg'],
    ['wht', 'assets/images/community/icon-wht.svg'],
    ['stamp-duty', 'assets/images/community/icon-stampduty.svg'],
    ['transfer-pricing', 'assets/images/community/icon-transferpricing.svg'],
    ['company-tax', 'assets/images/community/icon-companytax.svg'],
    ['others', 'assets/images/community/icon-others.svg'],
]);
export const COMMUNITY_DEFAULT_TOPIC_ICON = 'assets/images/community/icon-others.svg';

/** The backend `Language` enum values topic names are tagged with. */
export const COMMUNITY_TOPIC_LANGUAGE = { english: 1, chinese: 2 } as const;

/**
 * The language code the terms are published under for each app language. The terms exist in English, Chinese and Malay; the app has no
 * Malay interface, so Malay terms are reached only by a language code the backend is asked for explicitly.
 */
export function toTermsLanguageCode(appLanguage: string | null | undefined): string {
    return (appLanguage ?? '').toLowerCase().startsWith('zh') ? 'zh' : 'en';
}

/** The backend error code carried by an HTTP error, or undefined when it has none (a network failure, for example). */
export function getCommunityErrorCode(error: unknown): string | undefined {
    const code = (error as { error?: { error?: { code?: unknown } } } | null | undefined)?.error?.error?.code;
    return typeof code === 'string' ? code : undefined;
}

export function isCommunityErrorCode(code: string | undefined): boolean {
    return !!code && COMMUNITY_ERROR_CODE_PREFIXES.some((prefix) => code.startsWith(prefix));
}
